import { useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { currentMonth, lastNDays, periodLabel, type Period } from '@/lib/format';
import { Button } from './Button';

export interface DateRangePickerProps {
  value: Period;
  onChange: (period: Period) => void;
  className?: string;
  id?: string;
  disabled?: boolean;
}

const PRESETS: { label: string; build: () => Period }[] = [
  { label: 'Últimos 7 dias', build: () => lastNDays(7) },
  { label: 'Últimos 14 dias', build: () => lastNDays(14) },
  { label: 'Últimos 30 dias', build: () => lastNDays(30) },
  { label: 'Últimos 90 dias', build: () => lastNDays(90) },
  { label: 'Mês atual', build: () => currentMonth() },
];

export function DateRangePicker({ value, onChange, className, id, disabled }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Period>(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const invalid = draft.from > draft.to;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-line bg-surface px-3 text-sm text-fg transition',
          'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className="flex items-center gap-2 truncate">
          <CalendarDays className="h-4 w-4 shrink-0 text-muted" aria-hidden />
          <span className="truncate">{periodLabel(value)}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Selecionar período"
          className="absolute right-0 z-40 mt-1 w-[min(20rem,90vw)] rounded-lg border border-line bg-surface p-3 shadow-pop"
        >
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const period = preset.build();
                  setDraft(period);
                  onChange(period);
                  setOpen(false);
                }}
                className="rounded-md border border-line px-2 py-1 text-xs text-muted transition hover:bg-elevated hover:text-fg"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs text-muted">
              Início
              <input
                type="date"
                value={draft.from}
                max={draft.to}
                onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
                className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-fg"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted">
              Fim
              <input
                type="date"
                value={draft.to}
                min={draft.from}
                onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
                className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-fg"
              />
            </label>
          </div>

          {invalid && <p className="mt-2 field-error">A data inicial deve ser anterior à final.</p>}

          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={invalid}
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
            >
              Aplicar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
